import cn from 'classnames';
import React, { useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { useTranslateMessage } from '../../../../localization/useTranslateMessage';
import StandardOrSmall from '../../../../StandardOrSmall';
import { useOrderContext } from '../../../../useOrderContext';
import ProductEntrySmall from './small-screen/ProductEntrySmall';
import StandardProductEntry from './standard-screen/StandardProductEntry';
import SlumpWarning from '../../../../tickets/SlumpWarning';
import { useMemo } from 'react';

const Styled = styled.div``;

const ProductEntryConcrete = ({
  className = '',
  handleChange,
  primaryLineItem,
  otherLineItems,
  index,
  orderRef,
  linkedOrder,
}) => {
  const {
    form: { form },
    order: { readOnly },
    saveOrderBusy,
  } = useOrderContext();

  const disabled = React.useMemo(() => !form?.locationRef || saveOrderBusy, [form?.locationRef, saveOrderBusy]);

  const [lineItemBeingEdited, setLineItemBeingEdited] = useState(null);
  const [slumpWarning, setSlumpWarning] = useState(null);

  const initiateEdit = React.useCallback(
    id => {
      if (!disabled) {
        setLineItemBeingEdited(id);
      }
    },
    [disabled]
  );

  React.useEffect(() => {
    if (disabled) {
      setLineItemBeingEdited(null);
    }
  }, [disabled]);

  const handleEditCancel = React.useCallback(() => {
    setLineItemBeingEdited(null);
  }, []);

  const translateMessage = useTranslateMessage();

  const slumpLimitValidation = React.useCallback(productItem => {
    if (!productItem) {
      return;
    }

    const slump = productItem?.properties?.slump;
    const productLimits = productItem?.item?.limits;

    if (slump && productLimits) {
      const min = Number(productLimits?.consistence?.minimumValue ?? 0);
      const max = Number(productLimits?.consistence?.maximumValue ?? 1000);

      if (Number(slump) < min || Number(slump) > max) {
        setSlumpWarning(
          translateMessage({
            stringId: 'slumpWarning',
            defaultMessage: `Slump outside of range: min {min, number} and max {max, number}`,
            values: { min, max },
          })
        );
      } else {
        setSlumpWarning(null);
      }
    } else {
      setSlumpWarning(null);
    }
  }, []);

  const handleUpdateConcreteLineItem = React.useCallback(
    item => {
      handleChange('primaryLineItem', item);
      setLineItemBeingEdited(null);
      slumpLimitValidation(item);
    },
    [handleChange, slumpLimitValidation]
  );

  const handleUpdateOtherLineItems = React.useCallback(
    item => {
      if (item._id) {
        handleChange(
          'otherLineItems',
          otherLineItems.map(p => (p._id === item._id ? item : p))
        );
      } else {
        handleChange('otherLineItems', [...otherLineItems, { ...item, _id: uuid() }]);
      }
      setLineItemBeingEdited(null);
    },
    [handleChange, otherLineItems]
  );

  const handleDeleteRow = React.useCallback(
    id => {
      handleChange(
        'otherLineItems',
        otherLineItems.filter(item => item?._id !== id)
      );
    },
    [handleChange, otherLineItems]
  );

  const handleAddRow = React.useCallback(() => {
    handleChange('otherLineItems', [...otherLineItems, { _id: uuid() }]);
  }, [handleChange, otherLineItems]);

  const tickets = useMemo(
    () => (linkedOrder ? linkedOrder?.tickets || [] : form?.tickets),
    [linkedOrder, form?.tickets]
  );

  return (
    <Styled className={cn('product-entry', className)}>
      <StandardOrSmall breakpoint="800">
        <StandardProductEntry
          concreteLineItem={primaryLineItem}
          otherLineItems={otherLineItems}
          handleUpdateConcreteLineItem={handleUpdateConcreteLineItem}
          handleUpdateOtherLineItems={handleUpdateOtherLineItems}
          handleDeleteRow={handleDeleteRow}
          onChange={handleChange}
          disabled={disabled}
          onAddRow={handleAddRow}
          readOnly={readOnly}
          tickets={tickets}
          index={index}
          orderRef={orderRef}
          linkedOrder={linkedOrder}
        />
        <ProductEntrySmall
          lineItem={primaryLineItem}
          otherLineItems={otherLineItems}
          lineItemBeingEdited={lineItemBeingEdited}
          handleEditCancel={handleEditCancel}
          handleUpdateConcreteLineItem={handleUpdateConcreteLineItem}
          handleUpdateOtherLineItems={handleUpdateOtherLineItems}
          handleDeleteRow={handleDeleteRow}
          initiateEdit={initiateEdit}
          disabled={disabled}
          readOnly={readOnly}
          tickets={tickets}
        />
      </StandardOrSmall>
      {slumpWarning && <SlumpWarning message={slumpWarning} />}
    </Styled>
  );
};

export default ProductEntryConcrete;
